import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  createColumnHelper,
  rowPaginationFeature,
  rowSortingFeature,
  tableFeatures,
  useTable,
  type PaginationState,
  type SortingState,
} from "@tanstack/react-table";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import { defaultSiteId, useHosts, useSites } from "../api/queries";
import type { IFilter, Session } from "../api/models";
import { useSession } from "../auth/session";
import { cloneFilter, defaultAdvancedFilter, type AdvancedFilter } from "../filters/filter-logic";
import { useFloorGames } from "../floor/api";
import { buildFeatures } from "../floor/floor-logic";
import { FloorMap } from "../floor/FloorMap";
import { inPlayKey, useActiveGames, useInPlaySessions, useSelectedSession } from "../in-play/api";
import { FilterSidebar, type FilterTab } from "../in-play/FilterSidebar";
import { EMPTY_MAP, locate, zoomIn, zoomOut, type MapState } from "../in-play/map-selection";
import { buildHostFilter, buildOpenSessionsOnly, buildQuickFilter, defaultQuickFilter, QUICK_FILTERS, type QuickFilterCode } from "../in-play/quick-filters";
import { SessionCard } from "../in-play/SessionCard";
import { ActionsSidebar, SELECTED_LOCATION_SX, SelectedSessionSidebar } from "../in-play/SessionSidebars";
import { readStoredFilter, toStored, writeStoredFilter, type AppliedFilter } from "../in-play/stored-filter";
import { Breadcrumb } from "../ui/Breadcrumb";
import { PButton } from "../ui/PButton";
import { PagedDataView, type DataViewLayout } from "../ui/PagedDataView";
import { PSidebar } from "../ui/PSidebar";
import { SortByPopup } from "../ui/SortByPopup";

// PORT OF `src/app/_pages/in-play/in-play.component.html`, CLASS FOR CLASS:
//   .parent > .child [breadcrumb row + selected location + Sites] [floor map, hidden until "Map"]
//                    [toolbar: filter group | sort group | Map]
//           > .child-grow.card.p-0.shadow-1 [paged data view of session cards]
// PLUS THE FILTER, SITES, SELECTED-SESSION AND ACTIONS SIDEBARS.

// SORTING AND PAGING STATE IS TANSTACK TABLE'S (HEADLESS); THE ROWS RENDER AS CARDS, AS
// THE ANGULAR DATA VIEW DOES. COLUMN IDS ARE THE API'S `order_by` VALUES.
const features = tableFeatures({ rowSortingFeature, rowPaginationFeature });
const helper = createColumnHelper<typeof features, Session>();
const columns = helper.columns([
  helper.accessor((r) => r.playerName ?? "", { id: "name" }),
  helper.accessor((r) => r.description ?? "", { id: "machine" }),
  helper.accessor((r) => r.duration ?? 0, { id: "duration" }),
  helper.accessor((r) => r.gains ?? 0, { id: "gains" }),
  helper.accessor((r) => r.totalDailyTheo ?? 0, { id: "totalDailyTheo" }),
]);

// THE ANGULAR `sortBy_items`
const SORT_OPTIONS = [
  { label: "Player", value: "name" },
  { label: "Machine", value: "machine" },
  { label: "Duration", value: "duration" },
  { label: "Actual", value: "gains" },
  { label: "Theo", value: "totalDailyTheo" },
];

const PAGE_SIZE = 10;

// THE ROUTE-PARAM FILTER OF THE ANGULAR CONSTRUCTOR, AS THE ADVANCED FILTER IT IS THERE: THE
// PLAYER SECTION ON WITH THAT ONE HOST, SO THE ADVANCED TAB OPENS SHOWING IT
function hostAdvancedFilter(hostId: number): AdvancedFilter {
  const f = defaultAdvancedFilter();
  f.player = { ...f.player, enabled: true, hostIds: [hostId] };
  f.active = true;
  f.filters = buildHostFilter(hostId);
  return f;
}

export function InPlayPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const session = useSession();
  const sites = useSites();
  const games = useFloorGames();

  const allowed = useMemo(
    () => new Set<QuickFilterCode>(QUICK_FILTERS.filter((q) => session.hasRole(q.role)).map((q) => q.code)),
    [session],
  );
  const hostId = session.hostUser?.id ?? null;

  // A MANAGER'S DASHBOARD HOST TILE LANDS HERE AS `?userId=`. AS IN ANGULAR, AN ID THAT
  // MATCHES NO DISPLAYED HOST IS IGNORED AND THE PAGE OPENS AS USUAL.
  const { userId: routeUserId } = useSearch({ strict: false });
  const hosts = useHosts();
  const routeHost = routeUserId ? (hosts.data?.find((h) => h.userId === routeUserId) ?? null) : null;

  // THE LAST FILTER (`LAST_IN_PLAY_FILTER`) IS WHERE THE PAGE STARTS; `null` IS THE DEFAULT
  const [applied, setApplied] = useState<AppliedFilter | null>(readStoredFilter);
  // THE HOST FILTER WINS UNTIL THE USER PICKS A FILTER OR CLEARS
  const [picked, setPicked] = useState(false);
  const [chosenSite, setChosenSite] = useState<number | null>(null);
  const [sorting, setSorting] = useState<SortingState>([{ id: "totalDailyTheo", desc: true }]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: PAGE_SIZE });
  const [layout, setLayout] = useState<DataViewLayout>("grid");
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterTab, setFilterTab] = useState<FilterTab>("ft_quick_filters");
  const [sitesOpen, setSitesOpen] = useState(false);
  const [sortAnchor, setSortAnchor] = useState<HTMLElement | null>(null);

  // FLOOR MAP, SELECTED SESSION, ACTIONS
  const [mapVisible, setMapVisible] = useState(false);
  const [map, setMap] = useState<MapState>(EMPTY_MAP);
  const [selected, setSelected] = useState<Session | null>(null);
  const [actionSession, setActionSession] = useState<Session | null>(null);
  // ANGULAR STOPS EVERY POLL WHILE THE ACTIONS SIDEBAR IS OPEN
  const paused = actionSession !== null;

  // WHAT IS APPLIED, RESOLVED. DERIVED, NOT STORED: UNTIL THE USER PICKS, THE DEFAULT FOLLOWS
  // WHATEVER ROLES LOADED. A STORED QUICK FILTER THE USER MAY NO LONGER USE FALLS BACK TO IT.
  const effective: AppliedFilter | null = (() => {
    if (!picked && routeHost) return { kind: "advanced", filter: hostAdvancedFilter(routeHost.id) };
    if (applied?.kind === "quick" && allowed.has(applied.code) && buildQuickFilter(applied.code, hostId) !== null) return applied;
    if (applied && applied.kind !== "quick") return applied;
    const code = session.ready ? defaultQuickFilter(allowed, hostId) : null;
    return code ? { kind: "quick", code } : null;
  })();
  const filters: IFilter[] | null =
    effective === null
      ? null
      : effective.kind === "cleared"
        ? buildOpenSessionsOnly()
        : effective.kind === "quick"
          ? buildQuickFilter(effective.code, hostId)
          : effective.filter.filters;
  // THE TOOLBAR LABEL: THE QUICK FILTER'S NAME, "ADVANCED FILTER", OR NOTHING WHEN CLEARED
  const filterLabel =
    effective?.kind === "quick" ? (QUICK_FILTERS.find((q) => q.code === effective.code)?.label ?? "") : effective?.kind === "advanced" ? "Advanced Filter" : "";
  const activeQuick = effective?.kind === "quick" ? effective.code : null;
  const advancedCurrent = effective?.kind === "advanced" ? effective.filter : defaultAdvancedFilter();

  // KEEP `LAST_IN_PLAY_FILTER` IN STEP WITH WHAT IS APPLIED; "CLEAR FILTER" REMOVES IT
  const stored = effective && filters ? toStored(effective, filterLabel, filters) : undefined;
  useEffect(() => {
    if (stored !== undefined) writeStoredFilter(stored);
  }, [stored]);

  const siteId = chosenSite ?? defaultSiteId(sites.data ?? []);
  const sort = sorting[0] ?? { id: "totalDailyTheo", desc: true };
  const sessions = useInPlaySessions(
    siteId,
    filters,
    { page: pagination.pageIndex + 1, pageSize: PAGE_SIZE, orderBy: sort.id, orderDir: sort.desc ? "desc" : "asc" },
    paused,
  );
  const activeGames = useActiveGames(siteId, filters, paused);
  const selectedQuery = useSelectedSession(selected, paused);
  const shownSession = selected ? (selectedQuery.data ?? selected) : null;

  const table = useTable({
    features,
    columns,
    data: sessions.data?.rows ?? [],
    getRowId: (r) => String(r.id),
    state: { sorting, pagination },
    onSortingChange: (updater) => {
      setSorting(updater);
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    },
    onPaginationChange: setPagination,
    manualSorting: true,
    manualPagination: true,
    rowCount: sessions.data?.totalRows ?? 0,
    enableSortingRemoval: false,
  });

  // `clearSelectedSession()`: SIDEBAR CLOSED, NO SELECTION, NO PULSE, WHOLE FLOOR
  const clearSelected = () => {
    setSelected(null);
    setMap(EMPTY_MAP);
  };

  // THE SELECTED SESSION ENDED (A NULL `get-active-session`) OR COULD NOT BE READ: DROP IT, AS
  // ANGULAR DOES
  const selectionGone = selected !== null && (selectedQuery.isError || (selectedQuery.isSuccess && selectedQuery.data === null));
  useEffect(() => {
    if (selectionGone) clearSelected();
  }, [selectionGone]);

  const leaveHostLink = () => {
    // DROP `?userId=` SO A RELOAD SHOWS WHAT IS ON SCREEN, NOT THE OLD HOST FILTER
    if (routeUserId) void navigate({ to: "/in-play", search: {}, replace: true });
  };
  const apply = (f: AppliedFilter) => {
    setApplied(f);
    setPicked(true);
    table.setPageIndex(0);
    // ANGULAR RESETS THE SORT TO THEO, DESCENDING, WITH EVERY FILTER CHANGE (`setDefaultPaging`)
    setSorting([{ id: "totalDailyTheo", desc: true }]);
    leaveHostLink();
  };
  const selectFilter = (code: QuickFilterCode) => apply({ kind: "quick", code });
  const applyAdvanced = (f: AdvancedFilter) => {
    apply({ kind: "advanced", filter: cloneFilter(f) });
    setFilterOpen(false);
  };
  const clearFilter = () => {
    apply({ kind: "cleared" });
    setFilterOpen(false);
  };

  const openProfile = (r: Session) => {
    // THE ANGULAR `RouteToPlayerProfile` FALLS BACK TO `player_id` FOR THE SAME REASON
    const cardId = r.playerId ?? r.cardId;
    if (!cardId) return;
    void navigate({
      to: "/in-play/$playerId",
      params: { playerId: cardId },
      state: r.cmsPlayerId ? { expectedCmsPlayerId: r.cmsPlayerId } : {},
    });
  };

  // A CARD'S LOCATION BUTTON: OPEN THE MAP AND SHOW THE MACHINE (`showSessionLocation`)
  const showLocation = (r: Session) => {
    setMapVisible(true);
    const next = locate(map, games.data ?? [], r.location);
    if (!next) return;
    setMap(next);
    setSelected(r);
  };
  const toggleMap = () => {
    if (mapVisible) clearSelected();
    setMapVisible(!mapVisible);
  };
  const closeActions = () => {
    setActionSession(null);
    // POLLING RESUMES WITH AN IMMEDIATE REFRESH, AS `startPollingSessions()` DOES
    void qc.invalidateQueries({ queryKey: inPlayKey("sessions") });
    void qc.invalidateQueries({ queryKey: inPlayKey("active-games") });
  };

  if (session.error || sites.error) {
    return <Alert severity="error">In Play could not load your roles, hosts or sites. Please refresh.</Alert>;
  }
  // A `?userId=` LINK WAITS FOR HOSTS TOO, OR IT WOULD FIRST POLL THE DEFAULT FILTER
  if (!session.ready || sites.isPending || (routeUserId !== undefined && hosts.isPending)) {
    return (
      <div className="flex align-items-center justify-content-center" style={{ minHeight: "50vh" }}>
        <CircularProgress />
      </div>
    );
  }

  const data = sessions.data;
  const multipleSites = (sites.data?.length ?? 0) > 1;
  const sortLabel = SORT_OPTIONS.find((o) => o.value === sort.id)?.label ?? "Theo";
  const cleared = effective?.kind === "cleared";
  const mapFeatures = buildFeatures(activeGames.data ?? [], games.data ?? []);

  return (
    <div className="parent">
      <div className="child">
        <Breadcrumb
          items={[{ label: "In-Play" }]}
          actions={
            <div className="flex flex-row align-items-center gap-1 justify-content-end">
              <div>
                {shownSession && (
                  <PButton
                    label={shownSession.location}
                    icon="map-marker"
                    hostElement
                    className="font-bold min-width-100 bg-selected-location"
                    sx={SELECTED_LOCATION_SX}
                    onClick={() => setMap((m) => ({ ...m, sidebarOpen: !m.sidebarOpen }))}
                  />
                )}
              </div>
              <div className="pr-1">{multipleSites && <PButton label="Sites" icon="map" onClick={() => setSitesOpen(true)} />}</div>
            </div>
          }
        />

        {/* FLOOR MAP: ALWAYS MOUNTED (SO IT IS READY), SHOWN BY "MAP" */}
        <div className={`mt-2 mb-2 fadein animation-duration-1000${mapVisible ? "" : " hidden"}`}>
          <FloorMap svgId="inPlayMap" features={mapFeatures} pulse={map.pulse} zoomTo={map.zoomTo} />
        </div>

        <div className="mt-3 mb-2 p-1">
          <div className="grid">
            {/* FILTER GROUP + CURRENT FILTER LABEL */}
            <div className="col-4">
              <div className="flex flex-row align-items-center justify-content-start">
                <div className="p-inputgroup flex w-6rem">
                  <Tooltip title="Filter" placement="bottom">
                    <PButton icon="filter" ariaLabel="Filter" onClick={() => setFilterOpen((o) => !o)} sx={{ borderRadius: "4px 0 0 4px" }} />
                  </Tooltip>
                  <Tooltip title="Clear Filter" placement="bottom">
                    <span>
                      <PButton
                        icon="filter-slash"
                        ariaLabel="Clear Filter"
                        disabled={cleared}
                        onClick={clearFilter}
                        sx={{ borderRadius: "0 4px 4px 0", backgroundColor: "var(--primary-700)" }}
                      />
                    </span>
                  </Tooltip>
                </div>
                <div className="hidden md:block text-sm font-bold">{filterLabel}</div>
              </div>
            </div>
            {/* SORT DIRECTION + SORT BY */}
            <div className="col-4">
              <div className="p-inputgroup flex justify-content-end md:justify-content-center">
                <Tooltip title="Sort Direction" placement="bottom">
                  <PButton
                    icon={sort.desc ? "sort-amount-up-alt" : "sort-amount-down-alt"}
                    ariaLabel="Sort Direction"
                    onClick={() => table.setSorting([{ id: sort.id, desc: !sort.desc }])}
                    sx={{ borderRadius: "4px 0 0 4px" }}
                  />
                </Tooltip>
                <Tooltip title="Sort By" placement="bottom">
                  <PButton label={sortLabel} onClick={(e) => setSortAnchor(e.currentTarget)} sx={{ borderRadius: "0 4px 4px 0" }} />
                </Tooltip>
              </div>
            </div>
            {/* MAP */}
            <div className="col-4">
              <div className="flex align-items-center justify-content-end">
                <div>
                  <PButton label="Map" icon={mapVisible ? "arrow-circle-up" : "arrow-circle-down"} hostElement onClick={toggleMap} />
                </div>
              </div>
            </div>
          </div>
        </div>
        {sessions.isError && (
          <Alert severity="warning" className="mb-2">
            Could not refresh sessions. Retrying on the next poll.
          </Alert>
        )}
      </div>

      <div className="child-grow card p-0 shadow-1 overflow-x-hidden overflow-y-auto">
        {!filters ? (
          <div className="pillars-dataview-empty">
            Your account has no In Play filter available. My Active Players needs a host record for your user.
          </div>
        ) : (
          <PagedDataView
            rows={table.getRowModel().rows.map((r) => r.original)}
            page={pagination.pageIndex + 1}
            totalPages={Math.max(1, data?.totalPages ?? 1)}
            totalRows={data?.totalRows ?? 0}
            onPage={(p) => table.setPageIndex(p - 1)}
            layout={layout}
            onLayout={setLayout}
            emptyMessage="No Sessions available"
            rowKey={(r) => r.id}
            render={(r, l) => (
              <SessionCard session={r} layout={l} onProfile={() => openProfile(r)} onActions={() => setActionSession(r)} onLocation={() => showLocation(r)} />
            )}
          />
        )}
      </div>

      <FilterSidebar
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        tab={filterTab}
        onTab={setFilterTab}
        allowed={allowed}
        active={activeQuick}
        onSelect={selectFilter}
        current={advancedCurrent}
        onApplyAdvanced={applyAdvanced}
      />

      <SortByPopup
        anchorEl={sortAnchor}
        onClose={() => setSortAnchor(null)}
        value={sort.id}
        options={SORT_OPTIONS}
        onChange={(v) => table.setSorting([{ id: v, desc: sort.desc }])}
      />

      {/* THE SITES SIDEBAR: ONE PROPERTY AT A TIME, AS THE ANGULAR SITE SELECTOR KEEPS IT */}
      <PSidebar open={sitesOpen} onClose={() => setSitesOpen(false)} position="right" width={{ xs: "100%", md: "30rem" }} header={<span className="font-bold text-lg">Sites</span>}>
        <div className="card">
          {sites.data?.map((s, i) => (
            <div key={s.id} className={`flex justify-content-between align-items-center${i > 0 ? " mt-3" : ""}`}>
              <div>{s.name}</div>
              <Switch
                checked={s.id === siteId}
                onChange={() => {
                  setChosenSite(s.id);
                  table.setPageIndex(0);
                }}
                slotProps={{ input: { "aria-label": s.name } }}
              />
            </div>
          ))}
        </div>
      </PSidebar>

      <SelectedSessionSidebar
        open={map.sidebarOpen}
        session={shownSession}
        zoomedIn={map.zoomedIn}
        onClose={() => setMap((m) => ({ ...m, sidebarOpen: false }))}
        onZoomIn={() => setMap(zoomIn)}
        onZoomOut={() => setMap(zoomOut)}
        onCancel={clearSelected}
      />

      {/* ONE INSTANCE PER SESSION, SO A HALF-TYPED GREET OR NOTE NEVER MOVES TO ANOTHER PLAYER */}
      <ActionsSidebar key={actionSession ? `${actionSession.id}` : "none"} session={actionSession} onClose={closeActions} />
    </div>
  );
}
